import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, Upload, Plus, Trash2, ShieldCheck, Users, FileSpreadsheet, Eye, X } from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  adicionarInscrito,
  importarInscritosCsv,
  listarInscritos,
  listarSubmissions,
  removerInscrito,
  removerSubmission,
} from "@/lib/bolsa-feo/admin.functions";

export const Route = createFileRoute("/admin/bolsas-feo")({
  head: () => ({
    meta: [
      { title: "Admin — Bolsas FEO 2026" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/bolsas-feo">
      <BolsasFeoAdminPage />
    </AdminGate>
  ),
});

type Submission = {
  id: string;
  inscrito_id: string | null;
  email: string;
  nome: string;
  telefone: string | null;
  cpf: string | null;
  is_test: boolean;
  respostas: Record<string, string>;
  score: number;
  total: number;
  correct_ids: number[];
  duration_seconds: number | null;
  submitted_at: string;
};
type Inscrito = {
  id: string;
  email: string;
  nome: string;
  telefone: string | null;
  cpf: string | null;
  is_test: boolean;
  notes: string | null;
  created_at: string;
};


function BolsasFeoAdminPage() {
  const [tab, setTab] = useState<"submissions" | "inscritos">("submissions");
  const fnInscritos = useServerFn(listarInscritos);
  const fnSubs = useServerFn(listarSubmissions);
  const fnAddInsc = useServerFn(adicionarInscrito);
  const fnDelInsc = useServerFn(removerInscrito);
  const fnImport = useServerFn(importarInscritosCsv);
  const fnDelSub = useServerFn(removerSubmission);

  const inscritosQ = useQuery({ queryKey: ["bolsa-feo:inscritos"], queryFn: () => fnInscritos() });
  const subsQ = useQuery({ queryKey: ["bolsa-feo:subs"], queryFn: () => fnSubs() });

  const [newNome, setNewNome] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newFone, setNewFone] = useState("");
  const [newCpf, setNewCpf] = useState("");
  const [newIsTest, setNewIsTest] = useState(false);
  const [hideTests, setHideTests] = useState(false);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [openSub, setOpenSub] = useState<Submission | null>(null);

  const inscritos: Inscrito[] = inscritosQ.data?.inscritos ?? [];
  const submissions: Submission[] = (subsQ.data?.submissions ?? []) as Submission[];
  const totalQ = subsQ.data?.total_questions ?? 25;
  const gabarito: Record<number, string> = subsQ.data?.gabarito ?? {};

  const addInscrito = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNome.trim() || !newEmail.trim()) return toast.error("Nome e e-mail obrigatórios.");
    setBusy(true);
    try {
      await fnAddInsc({
        data: {
          nome: newNome,
          email: newEmail,
          telefone: newFone,
          cpf: newCpf,
          is_test: newIsTest,
        },
      });
      setNewNome(""); setNewEmail(""); setNewFone(""); setNewCpf(""); setNewIsTest(false);
      toast.success("Inscrito adicionado.");
      inscritosQ.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro.");
    } finally {
      setBusy(false);
    }
  };


  const importCsv = async () => {
    if (!csvFile) return toast.error("Selecione um CSV.");
    setBusy(true);
    try {
      const csv = await csvFile.text();
      const res = await fnImport({ data: { csv } });
      toast.success(`${res.imported} inscritos importados.`);
      if (res.errors?.length) toast.message(`${res.errors.length} avisos`, { description: res.errors.slice(0, 3).join("\n") });
      setCsvFile(null);
      inscritosQ.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao importar.");
    } finally {
      setBusy(false);
    }
  };

  const exportSubsCsv = () => {
    const rows: string[] = [];
    const head = ["nome", "email", "cpf", "telefone", "is_test", "nota", "total", "duracao_s", "submitted_at"];
    for (let i = 1; i <= totalQ; i++) head.push(`q${i}`);
    rows.push(head.join(","));
    for (const s of submissions) {
      const cells = [
        csvCell(s.nome), csvCell(s.email), csvCell(s.cpf ?? ""), csvCell(s.telefone ?? ""),
        s.is_test ? "1" : "0",
        String(s.score), String(s.total),
        s.duration_seconds != null ? String(s.duration_seconds) : "",
        s.submitted_at,
      ];
      for (let i = 1; i <= totalQ; i++) cells.push(s.respostas[String(i)] ?? "");
      rows.push(cells.join(","));

    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bolsa-feo-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen text-white">
      <div className="mx-auto max-w-6xl px-4 py-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/admin" className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> voltar ao painel
            </Link>
            <h1 className="mt-2 text-2xl font-bold flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-admin-accent" /> Bolsas FEO 2026 · Prova online
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Whitelist de inscritos + provas enviadas. Tabela secreta — visível apenas para a equipe admin.
            </p>
          </div>
          <Link
            to="/bolsas-feo-prova"
            target="_blank"
            className="text-xs rounded-md border border-white/10 bg-white/5 px-3 py-2 hover:bg-white/10"
          >
            Abrir página pública da prova ↗
          </Link>
        </div>

        <div className="flex items-center gap-2 border-b border-white/10">
          <TabBtn active={tab === "submissions"} onClick={() => setTab("submissions")}>
            Provas enviadas ({submissions.length})
          </TabBtn>
          <TabBtn active={tab === "inscritos"} onClick={() => setTab("inscritos")}>
            Inscritos autorizados ({inscritos.length})
          </TabBtn>
        </div>

        {tab === "submissions" && (() => {
          const visibleSubs = hideTests ? submissions.filter((s) => !s.is_test) : submissions;
          const realCount = submissions.filter((s) => !s.is_test).length;
          const testCount = submissions.length - realCount;
          return (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-white/60">
                <span className="font-semibold text-white">{realCount}</span> envios reais
                {testCount > 0 && (
                  <> · <span className="text-admin-accent">{testCount} de teste</span></>
                )}
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs text-white/70 inline-flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hideTests}
                    onChange={(e) => setHideTests(e.target.checked)}
                    className="accent-[#f5b022]"
                  />
                  ocultar testes
                </label>
                <Button variant="outline" size="sm" onClick={exportSubsCsv} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Exportar CSV
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">#</th>
                    <th className="text-left p-3">Nome / E-mail</th>
                    <th className="text-left p-3">CPF</th>
                    <th className="text-left p-3">WhatsApp</th>
                    <th className="text-right p-3">Nota</th>
                    <th className="text-right p-3">Duração</th>
                    <th className="text-left p-3">Enviado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleSubs.map((s, i) => (
                    <tr key={s.id} className={`border-t border-white/5 ${s.is_test ? "bg-amber-500/5" : ""}`}>
                      <td className="p-3 text-white/50">{i + 1}</td>
                      <td className="p-3">
                        <div className="font-semibold flex items-center gap-1.5">
                          {s.nome}
                          {s.is_test && (
                            <span className="text-[9px] uppercase tracking-wider bg-amber-500/20 text-admin-accent border border-amber-500/40 px-1.5 py-0.5 rounded">
                              teste
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/50">{s.email}</div>
                      </td>
                      <td className="p-3 text-xs font-mono text-white/70">{s.cpf ?? "—"}</td>
                      <td className="p-3 text-white/80">{s.telefone ?? "—"}</td>
                      <td className="p-3 text-right font-bold tabular-nums">
                        <span className={s.is_test ? "text-admin-accent" : s.score / s.total >= 0.6 ? "text-emerald-300" : "text-white"}>
                          {s.score}/{s.total}
                        </span>
                      </td>
                      <td className="p-3 text-right text-xs text-white/60 tabular-nums">
                        {s.duration_seconds != null ? `${Math.floor(s.duration_seconds / 60)}m ${s.duration_seconds % 60}s` : "—"}
                      </td>
                      <td className="p-3 text-xs text-white/60">{new Date(s.submitted_at).toLocaleString("pt-BR")}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => setOpenSub(s)} className="text-xs inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 hover:bg-white/10">
                          <Eye className="h-3 w-3" /> ver
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm("Apagar esta submissão?")) return;
                            await fnDelSub({ data: { id: s.id } });
                            subsQ.refetch();
                          }}
                          className="ml-1 text-xs inline-flex items-center gap-1 rounded border border-red-500/30 text-red-300 px-2 py-1 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleSubs.length === 0 && (
                    <tr><td colSpan={8} className="p-6 text-center text-white/50">Nenhuma prova enviada ainda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}


        {tab === "inscritos" && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <form onSubmit={addInscrito} className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Adicionar um inscrito</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div><Label className="text-white/70 text-xs">Nome</Label><Input value={newNome} onChange={(e) => setNewNome(e.target.value)} className="bg-white/5 border-white/10 text-white" /></div>
                  <div><Label className="text-white/70 text-xs">E-mail</Label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="bg-white/5 border-white/10 text-white" /></div>
                  <div><Label className="text-white/70 text-xs">CPF (acesso)</Label><Input value={newCpf} onChange={(e) => setNewCpf(e.target.value)} placeholder="000.000.000-00" className="bg-white/5 border-white/10 text-white" /></div>
                  <div><Label className="text-white/70 text-xs">WhatsApp</Label><Input value={newFone} onChange={(e) => setNewFone(e.target.value)} className="bg-white/5 border-white/10 text-white" /></div>
                </div>
                <label className="flex items-center gap-2 text-xs text-white/80 rounded border border-amber-500/30 bg-amber-500/5 p-2 cursor-pointer">
                  <input type="checkbox" checked={newIsTest} onChange={(e) => setNewIsTest(e.target.checked)} className="accent-[#f5b022]" />
                  <span><strong className="text-admin-accent">Inscrito de teste</strong> — pode realizar a prova várias vezes; envios marcados como teste.</span>
                </label>
                <Button type="submit" disabled={busy} className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold w-full">Adicionar</Button>
              </form>


              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Importar CSV</h3>
                <p className="text-xs text-white/60">CSV com colunas <code className="text-white/80">nome</code>, <code className="text-white/80">email</code>, <code className="text-white/80">cpf</code> (recomendado — usado como chave de acesso) e <code className="text-white/80">telefone</code> (opcional). Aceita vírgula, ponto-e-vírgula ou tab.</p>
                <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)} className="bg-white/5 border-white/10 text-white" />
                <Button onClick={importCsv} disabled={busy || !csvFile} className="w-full bg-white/10 hover:bg-white/15 border border-white/10">
                  <Upload className="h-4 w-4 mr-2" /> Importar
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3"><Users className="inline h-3.5 w-3.5" /></th>
                    <th className="text-left p-3">Nome</th>
                    <th className="text-left p-3">CPF</th>
                    <th className="text-left p-3">E-mail</th>
                    <th className="text-left p-3">WhatsApp</th>
                    <th className="text-left p-3">Cadastrado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {inscritos.map((i, idx) => (
                    <tr key={i.id} className={`border-t border-white/5 ${i.is_test ? "bg-amber-500/5" : ""}`}>
                      <td className="p-3 text-white/50">{idx + 1}</td>
                      <td className="p-3 font-semibold">
                        <div className="flex items-center gap-1.5">
                          {i.nome}
                          {i.is_test && (
                            <span className="text-[9px] uppercase tracking-wider bg-amber-500/20 text-admin-accent border border-amber-500/40 px-1.5 py-0.5 rounded">
                              teste
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-xs font-mono text-white/70">{i.cpf ?? "—"}</td>
                      <td className="p-3 text-white/80">{i.email}</td>
                      <td className="p-3 text-white/80">{i.telefone ?? "—"}</td>
                      <td className="p-3 text-xs text-white/60">{new Date(i.created_at).toLocaleDateString("pt-BR")}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={async () => {
                            if (!confirm(`Remover ${i.nome} da lista de inscritos?`)) return;
                            await fnDelInsc({ data: { id: i.id } });
                            inscritosQ.refetch();
                          }}
                          className="text-xs inline-flex items-center gap-1 rounded border border-red-500/30 text-red-300 px-2 py-1 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-3 w-3" /> remover
                        </button>
                      </td>
                    </tr>
                  ))}
                  {inscritos.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-white/50">Nenhum inscrito ainda. Adicione manualmente ou importe o CSV.</td></tr>

                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {openSub && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenSub(null)}>
          <div className="bg-[#13123a] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#13123a] border-b border-white/10 p-4 flex items-center justify-between">
              <div>
                <div className="font-bold">{openSub.nome}</div>
                <div className="text-xs text-white/60">{openSub.email} · Nota {openSub.score}/{openSub.total}</div>
              </div>
              <button onClick={() => setOpenSub(null)} className="text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4">
              <table className="w-full text-sm">
                <thead className="text-xs text-white/60">
                  <tr><th className="text-left p-2">#</th><th className="text-left p-2">Resposta</th><th className="text-left p-2">Gabarito</th><th className="text-left p-2">OK</th></tr>
                </thead>
                <tbody>
                  {Array.from({ length: totalQ }).map((_, idx) => {
                    const id = idx + 1;
                    const r = openSub.respostas[String(id)] ?? "—";
                    const g = gabarito[id] ?? "—";
                    const ok = r === g;
                    return (
                      <tr key={id} className="border-t border-white/5">
                        <td className="p-2 text-white/50">Q{id}</td>
                        <td className="p-2 uppercase font-mono">{r}</td>
                        <td className="p-2 uppercase font-mono text-white/60">{g}</td>
                        <td className="p-2">{ok ? "✅" : "❌"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${active ? "border-[#f5b022] text-white" : "border-transparent text-white/50 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

function csvCell(v: string): string {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
